import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../axiosInstance";
import { fetchTradeChatConfig, withTradeScriptVersion } from "../libs/tradeChatConfig";

const SCRIPT_ID = "trade-web-client-script";

const removeTradeWebScript = () => {
  const current = document.getElementById(SCRIPT_ID);
  if (current) {
    current.remove();
  }
};

const ensureTradeWebScript = async () => {
  if (document.getElementById(SCRIPT_ID)) return;
  const config = await fetchTradeChatConfig();
  if (document.getElementById(SCRIPT_ID)) return;
  if (!String(config.publicKey || "").trim()) {
    throw new Error("Server chưa cấu hình Public key.");
  }
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = withTradeScriptVersion(config.clientScriptUrl);
  script.async = true;
  script.setAttribute("data-token", config.publicKey);
  document.body.appendChild(script);
};

const truthyFlag = (value) =>
  value === true || value === 1 || value === "true" || value === "1";

const isStaffFromUser = (user) => {
  if (!user || typeof user !== "object") return false;
  const role = String(user.role || "").toLowerCase();
  if (role === "admin" || role === "administrator" || role === "member") {
    return true;
  }
  if (truthyFlag(user.isAdmin) || truthyFlag(user.admin)) return true;
  if (truthyFlag(user.isMember) || truthyFlag(user.member)) return true;
  return false;
};

/**
 * Load trade_web_client.js khi khách đã đăng nhập (không phải staff).
 * publicKey lấy từ server (Public key trong cài đặt hệ thống).
 */
const TradeWebChatClient = () => {
  const user = useSelector((state) => state.user.user);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn === true);
  const rehydrated = useSelector(
    (state) => state.user._persist?.rehydrated === true
  );
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!rehydrated) {
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    const applyLoadDecision = async (isStaff) => {
      if (cancelled || requestId !== requestIdRef.current) return;
      if (isStaff) {
        removeTradeWebScript();
        return;
      }
      try {
        await ensureTradeWebScript();
      } catch (err) {
        console.error("Trade chat client:", err?.message || err);
      }
    };

    if (isStaffFromUser(user)) {
      applyLoadDecision(true);
      return () => {
        cancelled = true;
        removeTradeWebScript();
      };
    }

    const token =
      typeof window !== "undefined" ? localStorage.getItem("token") : null;
    const hasSession = isLoggedIn && Boolean(token);

    if (!hasSession) {
      removeTradeWebScript();
      return () => {
        cancelled = true;
        removeTradeWebScript();
      };
    }

    (async () => {
      try {
        const response = await axiosInstance.get("/api/users/myFlags");
        const flags = response?.data?.data || {};
        const isStaff =
          flags.isAdmin === true ||
          flags.isMember === true ||
          isStaffFromUser(user);
        await applyLoadDecision(isStaff);
      } catch {
        await applyLoadDecision(isStaffFromUser(user));
      }
    })();

    return () => {
      cancelled = true;
      removeTradeWebScript();
    };
  }, [
    rehydrated,
    isLoggedIn,
    user,
    user?.role,
    user?.isAdmin,
    user?.isMember,
    user?.admin,
    user?.member,
  ]);

  return null;
};

export default TradeWebChatClient;
